import {
    CreativeXScoreFilters,
    PreflightStatusFilters,
    PublishedStatusFilter,
    BannerQaCommentedOnByFilter,
    BannerQaStatusFilter,
    BannerQaUserStatusFilter
} from "@/enums/filters";
import { BannerQaUserStatusLabelFilterMap } from "@/enums/banners";
import { defineStore } from "pinia";
import { constructLibraryFiltersQueryVariables } from "@/utils";
import { generateFilterForCollection, getActiveValuesOfCollectionFromQueryParams } from "@/utils/urls";
import { UrlFilterParams } from "@/enums/library";
import type { LocationQuery } from "vue-router";

export interface State {
    // Creatives filters
    selectedLanguages: string[];
    selectedSizes: string[];
    selectedEditableIdsByGroup: Record<string, string[]>;

    // Workflow filters
    creativeXStatus: (typeof PreflightStatusFilters)[keyof typeof PreflightStatusFilters];
    creativeXScore: (typeof CreativeXScoreFilters)[keyof typeof CreativeXScoreFilters];
    commentedOnBy: (typeof BannerQaCommentedOnByFilter)[keyof typeof BannerQaCommentedOnByFilter];
    qaStatus: (typeof BannerQaStatusFilter)[keyof typeof BannerQaStatusFilter];
    publishedStatus: (typeof PublishedStatusFilter)[keyof typeof PublishedStatusFilter];
    reviewStatus: (keyof typeof BannerQaUserStatusLabelFilterMap | "ALL")[];
    visibleTo: string;
    hideDuplicates: boolean;
    metadataSearch: Record<string, string>;

    allSelectedMode: boolean;
}

export const useCreativeLibraryFiltersStore = defineStore("creativeLibraryFilters", {
    state: (): State => ({
        selectedLanguages: [],
        selectedSizes: [],
        selectedEditableIdsByGroup: {},

        creativeXStatus: PreflightStatusFilters.All,
        creativeXScore: CreativeXScoreFilters.All,
        commentedOnBy: BannerQaCommentedOnByFilter.All,
        qaStatus: BannerQaStatusFilter.All,
        publishedStatus: PublishedStatusFilter.All,
        reviewStatus: [],
        visibleTo: "ME",
        hideDuplicates: false,
        metadataSearch: {},

        allSelectedMode: false
    }),

    getters: {
        getConstructedLibraryFilters: state => {
            return constructLibraryFiltersQueryVariables(state);
        },

        /** Compute library filters for query based on all selected mode */
        getComputedLibraryFilters: state => {
            return state.allSelectedMode ? constructLibraryFiltersQueryVariables(state) : null;
        },

        getUrlFilterParams: state => {
            return (filterOptions: {
                languages: { label: string; value: string }[];
                masterTemplates: { label: string; value: string }[];
                editableGroups: { name: string; options: { label: string; value: string }[] }[];
            }) => {
                const urlFilterParams = [
                    generateFilterForCollection(
                        UrlFilterParams.Language,
                        state.selectedLanguages,
                        filterOptions.languages.map(({ value }) => value)
                    ),
                    generateFilterForCollection(
                        UrlFilterParams.Size,
                        state.selectedSizes,
                        filterOptions.masterTemplates.map(({ value }) => value)
                    ),
                    {
                        key: UrlFilterParams.PublishedStatus,
                        value: state.publishedStatus === PublishedStatusFilter.All ? [] : [state.publishedStatus]
                    },
                    {
                        key: UrlFilterParams.Status,
                        value: state.qaStatus === BannerQaStatusFilter.All ? [] : [state.qaStatus]
                    },
                    {
                        key: UrlFilterParams.UserStatus,
                        value: state.reviewStatus.includes(BannerQaUserStatusFilter.All) ? [] : state.reviewStatus
                    },
                    {
                        key: UrlFilterParams.CommentedOnBy,
                        value: state.commentedOnBy === BannerQaCommentedOnByFilter.All ? [] : [state.commentedOnBy]
                    },
                    {
                        key: UrlFilterParams.VisibleTo,
                        value: state.visibleTo === "ME" ? [] : [state.visibleTo]
                    },
                    {
                        key: UrlFilterParams.Misc,
                        value: state.hideDuplicates ? "dedup" : []
                    },
                    {
                        key: UrlFilterParams.CreativeXScore,
                        value: state.creativeXScore === CreativeXScoreFilters.All ? [] : [state.creativeXScore]
                    },
                    {
                        key: UrlFilterParams.CreativeXStatus,
                        value: state.creativeXStatus === PreflightStatusFilters.All ? [] : [state.creativeXStatus]
                    },
                    ...Object.keys(state.metadataSearch).map(key => ({
                        key: `${UrlFilterParams.MetadataSearch}:${key}`,
                        value: state.metadataSearch[key] ? [state.metadataSearch[key]] : []
                    })),
                    ...filterOptions.editableGroups.map(editableGroup =>
                        generateFilterForCollection(
                            `${UrlFilterParams.GroupValue}:${editableGroup.name}`,
                            state.selectedEditableIdsByGroup[editableGroup.name] ?? [],
                            editableGroup.options.map(({ value }) => value)
                        )
                    )
                ];
                return urlFilterParams;
            };
        }
    },

    actions: {
        setSelectedGroupIdsByGroup(groupName: string, valueIds: string[]) {
            if (!valueIds.length) {
                delete this.selectedEditableIdsByGroup[groupName];
                return;
            }
            this.selectedEditableIdsByGroup[groupName] = valueIds;
        },

        setMetadataSearch(fieldId: string, search: string) {
            if (!search && this.metadataSearch[fieldId]) {
                delete this.metadataSearch[fieldId];
                return;
            }

            this.metadataSearch[fieldId] = search;
        },

        setInitialStateFromQuery(
            filterOptions: {
                languages: { label: string; value: string }[];
                masterTemplates: { label: string; value: string }[];
                editableGroups: { name: string; options: { label: string; value: string }[] }[];
            },
            query: LocationQuery
        ) {
            filterOptions.editableGroups.forEach(({ name, options }) => {
                const ids = getActiveValuesOfCollectionFromQueryParams({
                    key: `${UrlFilterParams.GroupValue}:${name}`,
                    allValues: options.map(({ value }) => value),
                    queryParams: query
                });

                this.setSelectedGroupIdsByGroup(name, ids);
            });

            this.selectedSizes = getActiveValuesOfCollectionFromQueryParams({
                key: UrlFilterParams.Size,
                allValues: filterOptions.masterTemplates.map(({ value }) => value),
                queryParams: query
            });

            // Set languages
            this.selectedLanguages = getActiveValuesOfCollectionFromQueryParams({
                key: UrlFilterParams.Language,
                allValues: filterOptions.languages.map(({ value }) => value),
                queryParams: query
            });

            this.publishedStatus = query[UrlFilterParams.PublishedStatus] ?? PublishedStatusFilter.All;
            this.qaStatus = query[UrlFilterParams.Status] ?? BannerQaStatusFilter.All;

            const reviewStatus = query[UrlFilterParams.UserStatus];

            if (Array.isArray(reviewStatus)) {
                this.reviewStatus = reviewStatus as State["reviewStatus"];
            } else if (reviewStatus) {
                this.reviewStatus = [reviewStatus as State["reviewStatus"][0]];
            } else {
                this.reviewStatus = [];
            }

            this.commentedOnBy = query[UrlFilterParams.CommentedOnBy] ?? BannerQaCommentedOnByFilter.All;
            this.visibleTo = query[UrlFilterParams.VisibleTo] ?? "ME";
            this.hideDuplicates = query[UrlFilterParams.Misc] === "dedup";
            this.creativeXScore = query[UrlFilterParams.CreativeXScore] ?? CreativeXScoreFilters.All;
            this.creativeXStatus = query[UrlFilterParams.CreativeXStatus] ?? PreflightStatusFilters.All;

            Object.keys(query).forEach(key => {
                if (key.startsWith(`${UrlFilterParams.MetadataSearch}:`)) {
                    const fieldId = key.split(":")[1];
                    const value = query[key];

                    if (!value || !fieldId) {
                        return;
                    }

                    const stringValue = Array.isArray(value) ? value[0] : value;

                    if (typeof stringValue === "string") {
                        this.setMetadataSearch(fieldId, stringValue);
                    }
                }
            });
        },

        clearAllFilters() {
            this.selectedLanguages = [];
            this.selectedSizes = [];
            this.selectedEditableIdsByGroup = {};
            this.creativeXStatus = PreflightStatusFilters.All;
            this.creativeXScore = CreativeXScoreFilters.All;
            this.commentedOnBy = BannerQaCommentedOnByFilter.All;
            this.qaStatus = BannerQaStatusFilter.All;
            this.publishedStatus = PublishedStatusFilter.All;
            this.reviewStatus = [];
            this.visibleTo = "ME";
            this.hideDuplicates = false;
            this.metadataSearch = {};
        },

        /**
         * Sync filters with campaign data to ensure no invalid filter values are selected
         * This might happen if a user has selected a filter value, then the campaign is updated
         * and that value is no longer valid (e.g. a language is removed from the campaign)
         */
        syncFilters(campaignData: {
            languages: string[];
            editableGroupValueIds: string[];
            masterTemplateIds: string[];
        }) {
            if (!this.selectedLanguages.every(language => campaignData.languages.includes(language))) {
                this.selectedLanguages = this.selectedLanguages.filter(language =>
                    campaignData.languages.includes(language)
                );
            }

            if (!this.selectedSizes.every(size => campaignData.masterTemplateIds.includes(size))) {
                this.selectedSizes = this.selectedSizes.filter(size => campaignData.masterTemplateIds.includes(size));
            }

            Object.keys(this.selectedEditableIdsByGroup).forEach(groupName => {
                if (
                    !this.selectedEditableIdsByGroup[groupName]?.every(id =>
                        campaignData.editableGroupValueIds.includes(id)
                    )
                ) {
                    this.setSelectedGroupIdsByGroup(
                        groupName,
                        this.selectedEditableIdsByGroup[groupName]!.filter(id =>
                            campaignData.editableGroupValueIds.includes(id)
                        )
                    );
                }
            });
        }
    }
});
